import { defaultSchema } from 'hast-util-sanitize';
import { visit } from 'unist-util-visit';
import { recogniseEmbed } from './embeds.js';
/**
 * What may survive from a page's HTML.
 *
 * The renderer used to pass author HTML straight through: `rehype-raw` in, no
 * sanitiser after. So a page containing `<script>`, an `onerror` handler, a
 * `javascript:` link or an arbitrary `<iframe>` produced exactly that in the
 * output — and that output is injected into the writer with
 * `dangerouslySetInnerHTML`, where the reader's session cookie lives. Content
 * here is written by importers and by agents as well as by people, so "the
 * author would not do that" is not an argument available to us.
 *
 * Built on GitHub's schema, which is the conservative one, plus exactly what this
 * renderer emits for its own directives. Notably NOT added:
 *
 * - `style`, anywhere. One aspect ratio was worth moving to a class rather than
 *   opening CSS injection on every page.
 * - `iframe`. Ours is materialised AFTER sanitising, from a placeholder, with the
 *   URL re-checked against the provider allowlist — so an author cannot forge one
 *   by writing the placeholder themselves.
 */
const CLASSED = [
    'div',
    'img',
    'label',
    'input',
    'span',
    'p',
    'section',
    'details',
    'summary',
    'figure',
    'figcaption',
    'ol',
    'ul',
    'li',
    'a',
    'code',
    'pre',
    'table',
    'blockquote',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
];
/**
 * Attributes this renderer emits and the default schema does not allow.
 *
 * `rel` on a link, because the unrecognised-embed fallback sets
 * `noopener noreferrer` and a link that loses it hands the opened page a handle
 * on ours. `loading` on an image, because a documentation page is mostly images
 * below the fold.
 *
 * `srcSet` and `sizes`, because the renderer offers a phone a smaller copy of a
 * screenshot than a desktop gets, and an allowance that stripped them would
 * have made that feature silently do nothing in the writer's preview while
 * working on the published site — the exact divergence this package exists to
 * prevent. They are widened deliberately and narrowly: a `srcset` value is a
 * list of IMAGE sources, which cannot execute anything, and `protocols` below
 * holds it to the same schemes `src` is already held to, so an author writing
 * raw HTML gains no reach they did not already have through `src`.
 *
 * `decoding` because the figure directive has always emitted it and this
 * schema has always thrown it away, so the preview and the site disagreed
 * about one attribute for no reason anybody chose.
 */
const EXTRA = {
    // `download` on the file-attachment block's link: without it the browser
    // navigates to the asset instead of saving it, which for a PDF or a zip is
    // the wrong default action.
    a: ['rel', 'target', 'download'],
    img: ['loading', 'decoding', 'width', 'height', 'title', 'srcSet', 'sizes'],
    details: ['open'],
};
function classed(tag) {
    const existing = ((defaultSchema.attributes ?? {})[tag] ?? []);
    const withoutClass = existing.filter((entry) => Array.isArray(entry) ? entry[0] !== 'className' : entry !== 'className');
    return [...withoutClass, ...(EXTRA[tag] ?? []), 'className'];
}
export const schema = {
    ...defaultSchema,
    // `label` because the tabs block (and now the code-group block) is labels
    // and radios; `figure`/`figcaption` because the figure directive is; `u` and
    // `mark` are the underline and highlight inline marks — GitHub's schema,
    // which this is built on, has no reason to allow either.
    tagNames: [...(defaultSchema.tagNames ?? []), 'figure', 'figcaption', 'label', 'u', 'mark'],
    /**
     * `disabled` is no longer forced onto every input.
     *
     * GitHub's schema requires it, which is right for their purpose — a rendered
     * task-list checkbox that looks tickable and saves nothing is a lie. It made
     * the tabs block inert: it is radios and labels and no JavaScript, which is
     * why it works with scripting disabled, and a disabled radio cannot be
     * selected.
     *
     * Task lists keep their own `disabled`: it comes from the Markdown-to-HTML
     * conversion, not from here.
     */
    required: Object.fromEntries(Object.entries(defaultSchema.required ?? {}).filter(([tag]) => tag !== 'input')),
    /**
     * `htmlFor` moves with `id`.
     *
     * The sanitiser prefixes `id` and `name` to stop content clobbering DOM
     * properties, and it does not touch `for` — so every label stopped pointing at
     * its input. The tabs block is CSS-only, built from radios and labels, and it
     * silently stopped switching. A protection that half-applies is a bug wearing a
     * security justification.
     */
    clobber: [...(defaultSchema.clobber ?? []), 'htmlFor'],
    attributes: {
        ...defaultSchema.attributes,
        /**
         * `role` and the aria attributes, on anything.
         *
         * This was here because the tabs block declared `role="tablist"`. It no
         * longer does — the role promised tab semantics a CSS-only radio group
         * cannot implement, and axe caught it — so nothing this renderer emits
         * depends on the allowance any more.
         *
         * It stays for the other direction: imported HTML carries roles that were
         * written on purpose, and stripping them makes a page LESS navigable for
         * somebody using assistive technology. An author writing a wrong role is a
         * worse-labelled page at most.
         */
        '*': [...((defaultSchema.attributes ?? {})['*'] ?? []), 'role', 'ariaHidden'],
        ...Object.fromEntries(CLASSED.map((tag) => [tag, classed(tag)])),
        /**
         * The tabs block is CSS-only: a radio per tab, a label per radio, and no
         * JavaScript at all — which is why it works with scripting disabled. The
         * default schema allows an input only as a disabled checkbox, which is right
         * for a task list and strips everything this needs.
         *
         * Allowing an author to write a radio is harmless: `form` is not allowed, so
         * there is nowhere for one to submit to.
         */
        /**
         * `dataLang` on both: the code group's persistence script (see
         * `remarkAdaDirectives`) matches a radio and its label by language rather
         * than by position, so a stored "python" finds the Python tab in every
         * group on the page, not just the one it was picked in.
         */
        label: [...classed('label'), 'htmlFor', 'dataLang'],
        input: [
            ['type', 'checkbox', 'radio'],
            'name',
            'id',
            'checked',
            'disabled',
            'className',
            'dataLang',
            /**
             * `dataValue` is `dataLang`'s twin for a NAMED tab group: what this tab
             * IS — `npm`, `macos` — so the same script can match it across every
             * group on the page that asks the same question.
             */
            'dataValue',
        ],
        /**
         * The embed placeholder. `data-embed` carries the URL the author wrote, and
         * it is re-validated after sanitising — an author writing this attribute by
         * hand gets the same allowlist check as one writing the directive.
         *
         * `data-sync-group` is the name of the question a tab group answers. An
         * author writing it by hand gets what the directive gets: their groups
         * follow each other, which is the whole of what it does.
         */
        /*
         * `dataIndex` is what makes a tabs block SHOW anything.
         *
         * The stylesheet reveals one panel with `.sls-tabs__panel[data-index='0']`
         * and hides the rest; stripped, no selector matched and every panel stayed
         * hidden. The published site was fine — it renders through its own pipeline
         * — so this was the EDITOR's preview showing a tab strip with nothing under
         * it, and the code group the same. Found by comparing the attributes the
         * renderer writes against the ones allowed here.
         */
        // `dataTab` and `dataPagefindIndexAttrs` carry a tab's title where the
        // site's search can read it — see the tabs renderer — and are text, not
        // behaviour; stripped, the preview still shows the tabs and the site's
        // index still gets them, since the site does not go through this.
        div: [
            ...classed('div'),
            'dataEmbed',
            'dataSyncGroup',
            'dataIndex',
            'dataTab',
            'dataPagefindIndexAttrs',
        ],
    },
    /**
     * `srcSet` is held to the schemes `src` is held to.
     *
     * `src` may only be http or https here, and `srcset` is the same kind of
     * value in the same position — so leaving it unchecked would have been a way
     * to write `data:` and `javascript:` sources on an image that `src` refuses.
     * Neither executes in an image, but "it does not execute" is not a reason to
     * leave a hole where the door already exists. Verified both ways in the
     * sanitiser tests.
     */
    protocols: {
        ...defaultSchema.protocols,
        srcSet: [...((defaultSchema.protocols ?? {})['src'] ?? [])],
    },
};
/**
 * Turn embed placeholders into frames, after sanitising.
 *
 * The order is the security property: the sanitiser cannot be given `iframe`,
 * because then an author could write one. So the directive renderer emits a
 * placeholder, the sanitiser keeps it as an ordinary div, and this puts the frame
 * in — with the URL checked against the provider allowlist again, so the only
 * srcs that can appear are ones `recogniseEmbed` produced.
 */
export function materialiseEmbeds() {
    return (tree) => {
        visit(tree, 'element', (node) => {
            const url = node.properties?.['dataEmbed'];
            if (typeof url !== 'string')
                return;
            const embed = recogniseEmbed(url);
            delete node.properties['dataEmbed'];
            if (!embed)
                return;
            node.children = [
                {
                    type: 'element',
                    tagName: 'iframe',
                    properties: {
                        src: embed.src,
                        title: embed.title,
                        /**
                         * Sandboxed, and the four privileges left out are the point: no
                         * top-level navigation, no forms, no downloads, no pointer lock. A
                         * player that can redirect the reader's browser is a redirect
                         * somebody else controls, on a page the customer is responsible for.
                         *
                         * `allow-same-origin` restores the PROVIDER's origin, not ours — the
                         * frame is cross-origin — and the players need it for their own
                         * storage.
                         */
                        // A list, because hast models space-separated attributes as one.
                        sandbox: [
                            'allow-scripts',
                            'allow-same-origin',
                            'allow-presentation',
                            'allow-popups',
                            'allow-popups-to-escape-sandbox',
                        ],
                        loading: 'lazy',
                        referrerpolicy: 'strict-origin-when-cross-origin',
                        allow: 'accelerometer; clipboard-write; encrypted-media; picture-in-picture',
                        allowFullScreen: true,
                    },
                    children: [],
                },
                ...node.children,
            ];
        });
    };
}
/**
 * In-page links, after the sanitiser has prefixed the ids it landed on.
 *
 * `hast-util-sanitize` rewrites `id` to `user-content-id` to stop content
 * clobbering DOM properties, and that is worth keeping — but it left every
 * `#anchor` link in a page pointing at an id that no longer existed. The
 * protection is the reason the ids move; this is the other half of it.
 */
export function repointAnchors(prefix = 'user-content-') {
    return (tree) => {
        visit(tree, 'element', (node) => {
            if (node.tagName !== 'a')
                return;
            const href = node.properties?.['href'];
            if (typeof href !== 'string' || !href.startsWith('#') || href.startsWith(`#${prefix}`))
                return;
            node.properties['href'] = `#${prefix}${href.slice(1)}`;
        });
    };
}
//# sourceMappingURL=sanitise.js.map